import _ from 'lodash';
import { Miscellaneous } from '@/constants/Miscellaneous';
import IMlsImportResult from '@/models/mls/IMlsImportResult';
import MlsPropertyItemUtils from '@/models/mls/MlsPropertyItemUtils';
import IMLSData, { newMlsData } from '@/models/mls/IMLSData';
import IMLSPropertyItem from '@/models/mls/IMLSPropertyItem';
import IMLSPropertyData from '@/models/mls/IMLSPropertyData';
import IMLSRawImportMlsInfo from '@/models/mls/IMLSRawImportMlsInfo';
import IMLSRawImport from '@/models/mls/IMLSRawImport';
import { Actions, Getters, mlsStore, store } from '../../store';
import GuideManager from '../GuideManager';
import { FieldNames } from '@/constants/FieldNames';
import LogUsageManager from '../LogUsageManager';
import IMlsImportStats, { newMlsImportStats } from '@/models/mls/IMlsImportStats';
import { MlsMapperEventKeys } from '@/constants/MlsMapperEventKeys';
import IComp from '@/models/Comp';
import ICompAddresses from '@/models/comp/ICompAddresses';
import { newMlsMapperMessage } from '@/models/mls/IMlsMapperMessage';
import IMlsPreviouslyViewed from '@/models/mls/IMlsPreviouslyViewed';
import CompsUtils from '@/models/comp/CompsUtils';
import MlsDataConverter from '@/converters/2.6/MlsDataConverter';
import { newProcessCompRequest } from '@/store/2.6/models/IProcessCompRequest';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import HostExchangeService from '@/services/2.6/HostExchangeService';

/**
 * MLS Import management.
 */
export default {
    /**
     * After launch MLS Mapper sends back a 'loaded' message indicating it's ready to receive additional values.
     * Note: The MLS mapper query parameters already includes the source, MLS token, p2id, usage Guid and form code.
     * @param contentWindow
     */
    postMessagesToIFrame(contentWindow: Window | null) {
        if (contentWindow) {
            postAddressesMessage(contentWindow);
            postImportLimitMessage(contentWindow);
        }
    },

    /**
     * Take the payload received from MLS Mapper and return an MlsImportResult type from it.
     * @param payload
     * @returns
     */
    getImportResultFromPayload(payload: string): IMlsImportResult {
        const importResult = payload as unknown as IMlsImportResult;
        return importResult;
    },

    /**
     * Saves the given import result to the store, including the MLS properties and the
     * CSV file info.
     * Stops adding when the import limit is reached but continues to update existing
     * MLS properties.
     * Saves the import stats to the store, containing various counts and address data on those added or replaced.
     * Uses the CompsManager to process the added & replaced MLS properties.
     * @param importResult
     * @returns the import stats containing various counts and address data on those added or replaced.
     */
    async processAndStore(importResult: IMlsImportResult): Promise<IMlsImportStats> {
        mapHeaderData(importResult.data);

        const importStats = newMlsImportStats();

        if (importResult.data.propertyData.length > 0) {
            importStats.count = importResult.data.propertyData.length;

            for (const propertyItem of importResult.data.propertyData) {
                const address1UnitNoZipCode = MlsPropertyItemUtils.getAddress1UnitNoAndZipCode(propertyItem);
                if (address1UnitNoZipCode.address1 && address1UnitNoZipCode.zipCode) {
                    const storePropertyItem = mlsStore.MlsPropertyByAddress1UnitNoZipCode(address1UnitNoZipCode);
                    if (storePropertyItem) {
                        fixupValues(propertyItem);
                        addMlsName(propertyItem, importResult.data.mlsInfo);

                        // This MLS property exists. Replace it. Does not count toward import limit.
                        await mlsStore.addOrReplaceProperty(propertyItem);
                        importStats.replaced++;
                        importStats.included.push(address1UnitNoZipCode);
                    } else {
                        // This MLS property does not exist. Check import limit before adding.
                        if (importStats.added < Miscellaneous.ImportLimit) {
                            fixupValues(propertyItem);
                            addMlsName(propertyItem, importResult.data.mlsInfo);

                            const added = await mlsStore.addOrReplaceProperty(propertyItem);
                            if (added) {
                                importStats.added++;
                                importStats.included.push(address1UnitNoZipCode);
                            }
                        } else {
                            importStats.ignored++;
                        }
                    }
                } else {
                    importStats.errors++;

                    const address = MlsPropertyItemUtils.getAddress(propertyItem);
                    LogUsageManager.importMlsDataError(address);
                }
            }

            if (importStats.added > 0 || importStats.replaced > 0) {
                const csvFileInfo = importResult.data.csvData ?? null;
                if (csvFileInfo) {
                    await mlsStore.addCsvFileInfo(csvFileInfo);
                }

                LogUsageManager.importedMlsData(importStats.added);

                GuideManager.guideCompleted(3);
            }
        }

        mlsStore.setImportStats(importStats);

        return importStats;
    },

    /**
     * Get an array of previously viewed for the address data in import stats
     * @param importResult
     * @returns
     */
    getExistingPreviouslyVieweds(): IMlsPreviouslyViewed[] {
        const previouslyVieweds: IMlsPreviouslyViewed[] = [];

        const importStats = mlsStore.MlsImportStats;
        if (importStats.included.length > 0) {
            const comps: IComp[] = store.getters[Getters.CompAddresses.Comps];

            for (const address1UnitNoZipCode of importStats.included) {
                const comp = CompsUtils.getByAddress1UnitNoAndZipCode(comps, address1UnitNoZipCode);
                if (comp && comp.hasVisited) {
                    const previouslyViewed: IMlsPreviouslyViewed = {
                        compNumber: comp.compNumber,
                        address1UnitNoZipCode: address1UnitNoZipCode,
                    };
                    previouslyVieweds.push(previouslyViewed);
                }
            }
        }

        return previouslyVieweds;
    },

    /**
     * Process the imported properties as comps, adding or replacing as needed.
     *
     * @param runPreferencesOnViewed A flag indicating whether existing viewed comps should have preferences run
     */
    async processToComps(runPreferencesOnViewed: boolean): Promise<void> {
        const importStats = mlsStore.MlsImportStats;

        const mlsPropertyData: IMLSPropertyData = [];
        for (const address1UnitNoZipCode of importStats.included) {
            const propertyItem = mlsStore.MlsPropertyByAddress1UnitNoZipCode(address1UnitNoZipCode);
            if (propertyItem) {
                mlsPropertyData.push(propertyItem);
            }
        }

        if (mlsPropertyData.length > 0) {
            const mlsComps = MlsDataConverter.convertForCompsUpdate(mlsPropertyData);

            const almCompId = theLocalStorageUtils.get(LocalStorageKeys.almCompId) ?? '';
            const hostExchangeService = HostExchangeService.getInstance();

            for (const mlsComp of mlsComps) {
                const compIdx: number = store.getters[Getters.CompAddresses.CompIdxByAddress](mlsComp.address);
                if (compIdx === -1) {
                    // Send the new comp address to our host for adding before processing
                    // Comp number is important here, give it the next in sequence
                    const compCount: number = store.getters[Getters.CompAddresses.CompCount];
                    mlsComp.compNumber = compCount;
                    await hostExchangeService.addCompAddress(almCompId, mlsComp);
                }

                const processCompRequest = newProcessCompRequest(mlsComp);
                processCompRequest.onUpdateRunPreferencesWhenHasVisited = runPreferencesOnViewed;

                store.dispatch(Actions.CompAddresses.ProcessComp, processCompRequest); // not awaiting
            }
        }
    },

    relaunchJumpStartModal(): boolean {
        const comps = store.getters[Getters.CompAddresses.Comps];
        if (comps.length === 0 || comps.length === 1) {
            return true;
        }
        return false;
    },
};

/**
 * Updates the given raw import, assuring each property source and related field has the headers
 * property value set if header index resolved to a value in the array of headers.
 * @param rawImport
 * @returns
 */
function mapHeaderData(rawImport: IMLSRawImport) {
    if (rawImport.propertyData.length >= 0) {
        for (const propertyItem of rawImport.propertyData) {
            for (const field of propertyItem.fields) {
                if (field.propertySource) {
                    for (const propertySource of field.propertySource) {
                        if (propertySource.headerIdx in rawImport.headers) {
                            const header = rawImport.headers[propertySource.headerIdx];
                            propertySource.headers = header;
                        }
                    }
                }

                if (field.relatedFields) {
                    for (const relatedField of field.relatedFields) {
                        if (relatedField.headerIdx in rawImport.headers) {
                            const header = rawImport.headers[relatedField.headerIdx];
                            relatedField.headers = header;
                        }
                    }
                }
            }
        }
    }
}

/**
 * Correct values in the given property item QS doesn't handle, like any NaNs.
 * @param propertyItem
 */
function fixupValues(propertyItem: IMLSPropertyItem) {
    for (const mlsData of propertyItem.fields) {
        if (typeof mlsData.formData === 'number' && isNaN(mlsData.formData)) {
            mlsData.formData = '';
        }
    }
}

/**
 * Add the MLS name to the given property item when it doesn't currently contain FieldNames.MlsImportName field.
 * @param propertyItem
 * @param mlsInfo
 */
function addMlsName(propertyItem: IMLSPropertyItem, mlsInfo: IMLSRawImportMlsInfo) {
    if (propertyItem && mlsInfo) {
        const fieldIdx = propertyItem.fields.findIndex((f) => f.formFieldName === FieldNames.MlsImportName);
        if (fieldIdx < 0) {
            const mlsData: IMLSData = newMlsData();
            mlsData.formFieldName = FieldNames.MlsImportName;
            mlsData.formData = mlsInfo.mlsName;

            propertyItem.fields.push(mlsData);
        }
    }
}

/**
 * Post the addresses message to MLS Mapper
 * @param contentWindow
 */
function postAddressesMessage(contentWindow: Window) {
    const compAddresses: ICompAddresses = [];
    const comps: IComp[] = store.getters[Getters.CompAddresses.Comps];
    for (const comp of comps) {
        const compAddress = _.clone(comp.address);
        compAddresses.push(compAddress);
    }

    const message = newMlsMapperMessage();
    message.key = MlsMapperEventKeys.addresses;
    message.data = JSON.stringify(compAddresses);

    const messageString = JSON.stringify(message);
    contentWindow.postMessage(messageString, '*');
}

/**
 * Post the import limit message to MLS Mapper
 * @param contentWindow
 */
function postImportLimitMessage(contentWindow: Window) {
    const message = newMlsMapperMessage();
    message.key = MlsMapperEventKeys.importLimit;
    message.data = `${Miscellaneous.ImportLimit}`;

    const messageString = JSON.stringify(message);
    contentWindow.postMessage(messageString, '*');
}
